import { Enum } from '../../../common/constants'
import { TenantForSidebarDto } from '../../MyTenant/tenantsModel'
import { getAdminErrorMessage } from '../adminErrorModel'
import { RebuildableJob, RebuildSnapshotsRequest, RebuildSnapshotsResult } from './rebuildSnapshotsApi'

export const RebuildScopes = { AllTenants: 'all-tenants', Tenant: 'tenant', Job: 'job' } as const
export type RebuildScope = Enum<typeof RebuildScopes>

export type JobOption = {
  value: number
  label: string
}

export const toJobOptions = (jobs: RebuildableJob[]): JobOption[] => {
  return [...jobs]
    .sort((a, b) => a.name.localeCompare(b.name))
    .map(job => ({
      value: job.id,
      label: `#${job.id} — ${job.name} (${job.customerName})`,
    }))
}

export const filterJobOption = (input: string, option?: JobOption): boolean => {
  return (option?.label ?? '').toLowerCase().includes(input.trim().toLowerCase())
}

export const toRebuildRequest = (
  scope: RebuildScope,
  tenantId: number | undefined,
  jobId: number | undefined
): RebuildSnapshotsRequest | null => {
  if (scope === RebuildScopes.AllTenants) {
    return null
  }
  if (scope === RebuildScopes.Tenant) {
    if (tenantId === undefined) {
      return null
    }
    return { tenantId }
  }
  if (jobId === undefined || !Number.isInteger(jobId) || jobId <= 0) {
    return null
  }
  return { jobId }
}

export const getConfirmTitle = (scope: RebuildScope, tenantName: string, jobLabel: string): string => {
  if (scope === RebuildScopes.Tenant) {
    return `Rebuild job snapshots for ${tenantName}?`
  }
  return `Rebuild the snapshot for ${jobLabel}?`
}

export const getQueuedMessage = (jobCount: number): string => {
  if (jobCount === 1) {
    return 'Queued a snapshot rebuild for 1 job.'
  }
  return `Queued snapshot rebuilds for ${jobCount} jobs.`
}

export const getRebuildErrorMessage = (error: unknown): string => {
  return getAdminErrorMessage(error, 'Failed to queue the snapshot rebuild. Please try again.')
}

export const needsTenantPicker = (scope: RebuildScope): boolean => scope !== RebuildScopes.AllTenants

export type TenantRebuildFailure = {
  tenantId: number
  tenantName: string
  message: string
}

export type AllTenantsProgress = {
  total: number
  done: number
  jobsQueued: number
  failures: TenantRebuildFailure[]
}

export const rebuildEachTenant = async (
  tenants: TenantForSidebarDto[],
  rebuild: (request: RebuildSnapshotsRequest) => Promise<RebuildSnapshotsResult>,
  onProgress: (progress: AllTenantsProgress) => void,
  shouldContinue: () => boolean = () => true
): Promise<AllTenantsProgress> => {
  const ordered = [...tenants].sort((a, b) => a.name.localeCompare(b.name))
  let progress: AllTenantsProgress = { total: ordered.length, done: 0, jobsQueued: 0, failures: [] }
  onProgress(progress)
  for (const tenant of ordered) {
    if (!shouldContinue()) {
      return progress
    }
    try {
      const result = await rebuild({ tenantId: tenant.id })
      progress = { ...progress, done: progress.done + 1, jobsQueued: progress.jobsQueued + result.jobCount }
    } catch (error) {
      const failure = { tenantId: tenant.id, tenantName: tenant.name, message: getRebuildErrorMessage(error) }
      progress = { ...progress, done: progress.done + 1, failures: [...progress.failures, failure] }
    }
    if (!shouldContinue()) {
      return progress
    }
    onProgress(progress)
  }
  return progress
}

export const getAllTenantsConfirmTitle = (tenantCount: number): string => {
  return `Rebuild job snapshots for all ${tenantCount} tenants?`
}

export const getAllTenantsProgressMessage = (progress: AllTenantsProgress): string => {
  const jobs = progress.jobsQueued === 1 ? '1 job' : `${progress.jobsQueued} jobs`
  return `${progress.done} of ${progress.total} tenants done, ${jobs} queued so far.`
}

export const getAllTenantsPercent = (progress: AllTenantsProgress): number => {
  if (progress.total === 0) {
    return 100
  }
  return Math.floor(progress.done / progress.total * 100)
}

export const getAllTenantsAlertType = (progress: AllTenantsProgress, isRunning: boolean): 'info' | 'warning' | 'success' => {
  if (isRunning) {
    return 'info'
  }
  return progress.failures.length > 0 ? 'warning' : 'success'
}
